import { observer } from 'mobx-react'
import { Input, Table } from 'antd'
import { useCallback, useState } from 'react'
import {
  Cartesian2, Cartesian3,
  // Cartographic, Math as CesiumMath,
  Color,
  HeightReference, LabelStyle, NearFarScalar, VerticalOrigin,
} from '@cesiumgs/cesium-analytics'
import type { ColumnsType } from 'antd/es/table'
import { debounce } from '@/utils/rightNavCtrl'
import useStore from '@/hooks/useStore'
import './index.less'
import pop_close from '@/assets/images/modal/pop-close.svg'

const Search = Input

interface DataType {
  key: number
  name: string
  address: string
  parcelX: string
  parcelY: string
  buildName: string
  jibunAddr: string
}

const searchEntityArr: any[] = []

function PoiSearch() {
  const mapStore = useStore('mapStore')
  const uiStore = useStore('uiStore')
  const facilityStore = useStore('facilityStore')
  const rightNavCtrlStore = useStore('rightNavCtrlStore')
  const [inputValue, setInputValue] = useState('')
  const [tableData, setTableData] = useState<DataType[]>([])
  const [total, setTotal] = useState<number>(0)
  const [resultShow, setResultShow] = useState(false)
  const [loading, setLoading] = useState(false)
  const [tableParams, setTableParams] = useState({
    pageSize: 10,
    pageNum: 1,
  })

  const columns: ColumnsType<DataType> = [
    {
      title: 'Index',
      dataIndex: 'key',
      key: 'key',
      align: 'center',
      width: '10%',
    },
    {
      title: 'Name',
      dataIndex: 'jibunAddr',
      key: 'jibunAddr',
    },
  ]

  const search = (value: string, pageNum: number = tableParams.pageNum, pageSize: number = tableParams.pageSize) => {
    if (!value)
      return
    setLoading(true)
    mapStore.queryPoiInfo({
      count: pageSize,
      page: pageNum,
      keyword: value,
    }).then((res: any) => {
      if (res.contents.code === '200') {
        if (res.contents.result.jusoList && res.contents.result.jusoList.jusoList && res.contents.result.jusoList.jusoList.length > 0) {
          const arr: DataType[] = []
          res.contents.result.jusoList.jusoList.forEach((item: any, index: number) => {
            arr.push({
              key: index + 1,
              name: item.buildName,
              address: item.jibunAddr,
              parcelX: item.parcelX,
              parcelY: item.parcelY,
              buildName: item.buildName,
              jibunAddr: item.jibunAddr,
            })
          })
          setTableData(arr)
          setTotal(res.contents.result.common.totalCount)
          setResultShow(true)
          rightNavCtrlStore.handleShowPolygonOnMap(false)
          generateEntity(arr)
        }
        else if (res.contents.result.jusoList && res.contents.result.jusoList.jusoList && Object.keys(res.contents.result.jusoList.jusoList).length > 0) {
          const { jusoList } = res.contents.result.jusoList
          const arr = [
            {
              key: 1,
              name: jusoList.buildName,
              address: jusoList.jibunAddr,
              parcelX: jusoList.parcelX,
              parcelY: jusoList.parcelY,
              buildName: jusoList.buildName,
              jibunAddr: jusoList.jibunAddr,
            },
          ]
          setTableData(arr)
          setTotal(1)
          setResultShow(true)
          rightNavCtrlStore.handleShowPolygonOnMap(false)
          generateEntity(arr)
        }
        else {
          setResultShow(false)
        }
      }
      else {
        setResultShow(false)
        rightNavCtrlStore.handleShowPolygonOnMap(true)
      }
    }).finally(() => {
      setLoading(false)
    })
  }

  const onChange = (e: any) => {
    const value = e.target.value
    setInputValue(value)
    if (value === '') {
      setResultShow(false)
      rightNavCtrlStore.handleShowPolygonOnMap(true)
    }
    else {
      debouncedSave(value)
    }
  }

  const resetInputValue = () => {
    setInputValue('')
    setResultShow(false)
    rightNavCtrlStore.handleShowPolygonOnMap(true)
    if (searchEntityArr.length > 0) {
      searchEntityArr.forEach((item: any) => {
        window.cviewer.entities.remove(item)
      })
    }
  }

  const debouncedSave = useCallback(debounce((value: any) => search(value), 800), [])

  const pageChange = (page: any) => {
    search(inputValue, page)
  }

  const onRow = (record: any, rowIndex: any) => {
    return {
      onClick: () => {
        handleRowClick(record, rowIndex)
      },
    }
  }

  const generateEntity = (arr: DataType[]) => {
    arr.forEach((item: DataType, index: number) => {
      const position = Cartesian3.fromDegrees(Number.parseFloat(item.parcelX), Number.parseFloat(item.parcelY), 2000)
      const boardObj = window.cviewer.entities.add({
        name: item.buildName,
        position,
        label: {
          text: item.buildName,
          font: '500 30px Helvetica', // 15pt monospace
          scale: 0.7,
          style: LabelStyle.FILL,
          fillColor: Color.GOLD,
          pixelOffset: new Cartesian2(0, -60), // offset
          showBackground: true,
          heightReference: HeightReference.CLAMP_TO_GROUND,
          backgroundColor: new Color(0, 0, 0, 0.7),
          scaleByDistance: new NearFarScalar(1.5e2, 1, 30000, 0.0),
        },
        billboard: {
          // image: imageArr[index], // default: undefined
          show: true, // default
          // pixelOffset: new Cartesian2(0, -10), // default: (0, 0)
          // eyeOffset: new Cartesian3(0.0, 0.0, 0.0), // default
          // horizontalOrigin: HorizontalOrigin.LEFT, // default
          heightReference: HeightReference.CLAMP_TO_GROUND,
          verticalOrigin: VerticalOrigin.BOTTOM, // default: CENTER
          scaleByDistance: new NearFarScalar(1.5e2, 1, 30000, 0.0),
          scale: 2, // default: 1.0
          // alignedAxis: Cartesian3.ZERO, // default
          width: 20, // default: undefined
          height: 24, // default: undefined
        },
      })
      searchEntityArr.push(boardObj)
    })
  }

  const handleRowClick = (record: any, rowIndex: number) => {
    window.cviewer.camera.flyTo({
      destination: Cartesian3.fromDegrees(Number(record.parcelX), Number(record.parcelY), 1000),
    })
  }

  return (
    <div className={'PoiSearch'}>
      <div className={'map-search'}>
        <Search value={inputValue} onChange={onChange} className={'inputPart'} placeholder={'주소 검색'} translate="no" />
        <span className={'search-img'} aria-hidden />
        {/* <span onClick={resetInputValue} className={'search-img-close'}/> */}
        <img className="search-img-close" onClick={resetInputValue} src={pop_close} alt="close" />
      </div>
      {
        resultShow
        && <div className={'map-search-resluts'}>
          <Table
            className={'results-table'}
            showHeader={false}
            columns={columns}
            dataSource={tableData}
            size={'small'}
            loading={loading}
            pagination={{
              position: ['bottomCenter'],
              total,
              showSizeChanger: false,
              onChange: pageChange,
            }}
            onRow={onRow}
          />
        </div>
      }
    </div>
  )
}

export default observer(PoiSearch)
